import { useCallback, useContext, useState } from 'react';

import { ConfigContext } from '@/Contexts/ConfigContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { CountryProps } from '@/models/Country';
import { CountyProps } from '@/models/County';
import { StateProps } from '@/models/State';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';

export default function useStatesAndCounties() {
  const { customFetch } = useCustomFetch();
  const { authResult } = useContext(TokenContext);

  const { allStates, allCountries, allCounties } = useContext(ConfigContext);

  const [fetchingStates, setFetchingStates] = useState<boolean>(false);
  const [fetchingCounties, setFetchingCounties] = useState<boolean>(false);
  const [fetchingCountries, setFetchingCountries] = useState<boolean>(false);

  const statesUrl = `${baseUrl}states`;
  const countiesUrl = `${baseUrl}counties`;
  const countriesUrl = `${baseUrl}countries`;

  const getAllStates = useCallback(async (): Promise<StateProps[] | null> => {
    if (allStates.length > 0) return allStates;

    if (fetchingStates) return [];
    setFetchingStates(true);
    const results = await customFetch<StateProps[]>(statesUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    setFetchingStates(false);
    if (typeof results === 'string') {
      return [];
    }

    return results;
  }, [authResult, allStates, customFetch, fetchingStates, statesUrl]);

  const getAllCounties = useCallback(async (): Promise<CountyProps[] | null> => {
    if (allCounties.length > 0) return allCounties;

    if (fetchingCounties) return [];
    setFetchingCounties(true);
    const results = await customFetch<CountyProps[]>(countiesUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    setFetchingCounties(false);
    if (typeof results === 'string') {
      return [];
    }
    return results;
  }, [authResult, allCounties, countiesUrl, customFetch, fetchingCounties]);

  const getCountiesInState = useCallback(
    (stateId?: string): CountyProps[] => {
      if (allCounties.length > 0) {
        return allCounties.filter((x: CountyProps) => x.stateId == stateId);
      } else {
        return [];
      }
    },
    [allCounties]
  );

  const getAllCountries = useCallback(async (): Promise<CountryProps[] | null> => {
    if (allCountries.length > 0) return allCountries;

    if (fetchingCountries) return [];
    setFetchingCountries(true);
    const response = await customFetch<CountryProps[]>(countriesUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    setFetchingCountries(false);

    if (typeof response === 'string') {
      return [];
    }

    return response;
  }, [authResult, allCountries, countriesUrl, customFetch, fetchingCountries]);

  const getStateIdByStateAbbreviation = useCallback(
    (stateAbbreviation: string, states: StateProps[]) => {
      let result: string | null = null;

      if (states.length > 0) {
        result =
          states?.find((state: StateProps) => state.abbrev === stateAbbreviation)?.id || null;
      }

      return result;
    },
    []
  );

  const getCountryIdByCountryAbbreviation = useCallback(
    (countryAbbreviation: string, countries: CountryProps[]) => {
      let result: string | number | null = null;

      if (countries.length > 0) {
        result =
          countries?.find(
            (country: CountryProps) =>
              country.abbrev.toLowerCase() === countryAbbreviation?.toLowerCase()
          )?.id || null;
      }

      return result;
    },
    []
  );

  return {
    getAllStates,
    getCountiesInState,
    getAllCountries,
    getAllCounties,
    getStateIdByStateAbbreviation,
    getCountryIdByCountryAbbreviation,
  };
}
